import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, RefreshCw, Loader2, Bell, CalendarClock, ListChecks,
  TrendingDown, CheckCircle2, ShieldAlert,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReminderRow({ item, tone, onDone }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] px-3 py-2" data-testid={`reminder-${item.meeting_id}-${item.index}`}>
      <div className="min-w-0">
        <div className="text-sm text-[#59554D]">{item.text}</div>
        <div className="text-xs text-[#8A857A] mt-0.5 truncate">
          {item.client_name}{item.due_date ? ` · due ${item.due_date}` : " · no due date"}{item.owner ? ` · ${item.owner}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {item.due_date && (
          <Badge className="border-0" style={{ background: `${tone}1A`, color: tone }}>{item.due_date}</Badge>
        )}
        <Button size="icon" variant="ghost" title="Mark done"
          className="h-7 w-7 text-[#8A857A] hover:text-[#0E6B3F]"
          onClick={() => onDone(item)} data-testid={`reminder-done-${item.meeting_id}-${item.index}`}>
          <CheckCircle2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function CustomerSuccessAtRisk() {
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState(null);
  const [atRisk, setAtRisk] = useState(null);
  const [sending, setSending] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        axios.get(`${API}/customer-success/action-reminders`),
        axios.get(`${API}/customer-success/at-risk`),
      ]);
      setReminders(r.data);
      setAtRisk(a.data);
    } catch (e) {
      toast.error("Failed to load At-Risk board");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendDigest = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/customer-success/action-reminders/send-digest`);
      toast.success(res.data.sent > 0 ? `Reminder emails sent to ${res.data.sent} rep(s)` : "No due actions to email right now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminders (email not configured?)");
    } finally {
      setSending(false);
    }
  };

  const markDone = async (item) => {
    try {
      await axios.patch(`${API}/customer-success/clients/${encodeURIComponent(item.client_id)}/meetings/${item.meeting_id}/actions/${item.index}`, { done: true });
      toast.success("Action marked done");
      fetchAll();
    } catch (e) {
      toast.error("Could not update action");
    }
  };

  const c = reminders?.counts || {};

  return (
    <div className="space-y-6" data-testid="cs-at-risk-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFF7D1] flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-[#6B5600]" />
          </div>
          <div>
            <h2 className="heading text-xl text-[#1A1A1A] tracking-tight">At-Risk & Reminders</h2>
            <p className="text-sm text-[#59554D]">Clients trending down and action items that need chasing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={sendDigest} disabled={sending}
            className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="at-risk-send-digest">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />} Email reminders now
          </Button>
          <Button variant="outline" onClick={fetchAll} disabled={loading}
            className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="at-risk-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-16 text-center text-[#8A857A]"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…</div>
      ) : (
        <>
          {/* At-risk clients */}
          <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-sm text-[#1A1A1A] font-medium mb-4">
                <ShieldAlert className="w-4 h-4 text-[#C23A00]" /> At-risk clients ({atRisk?.total || 0})
              </div>
              {(atRisk?.clients || []).length === 0 ? (
                <p className="text-sm text-[#8A857A] py-4 text-center">No clients are flagged — all healthy and on track. 🎉</p>
              ) : (
                <div className="space-y-2" data-testid="at-risk-list">
                  {atRisk.clients.map((cl) => (
                    <div key={cl.client_id} className="flex items-center justify-between gap-3 rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] px-3 py-2.5" data-testid={`at-risk-${cl.client_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-[#1A1A1A] font-medium truncate flex items-center gap-2">
                          {cl.client_name}
                          {(cl.last_hc_score ?? cl.last_health_score) != null && (
                            <Badge className="border-0 bg-[#DAD5CA] text-[#59554D]">
                              {cl.last_hc_score != null
                                ? `Health ${cl.last_hc_score}/100${cl.last_hc_band ? ` · ${cl.last_hc_band}` : ""}`
                                : `Health ${cl.last_health_score}/10`}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          {cl.reasons.map((r, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-xs text-[#6B5600]">
                              {r.toLowerCase().includes("dropped") ? <TrendingDown className="w-3 h-3" /> : r.toLowerCase().includes("action") ? <ListChecks className="w-3 h-3" /> : <CalendarClock className="w-3 h-3" />}
                              {r}{i < cl.reasons.length - 1 ? "" : ""}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Badge className="border-0 shrink-0" style={{ background: cl.severity >= 4 ? "#C23A001A" : "#6B56001A", color: cl.severity >= 4 ? "#C23A00" : "#6B5600" }}>
                        {cl.severity >= 4 ? "High" : "Watch"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reminders */}
          <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
            <CardContent className="p-5 space-y-5">
              <div className="flex items-center gap-2 text-sm text-[#1A1A1A] font-medium">
                <Bell className="w-4 h-4 text-[#8A6D00]" /> Action reminders
                <span className="text-xs text-[#8A857A] font-normal">
                  {c.overdue || 0} overdue · {c.due_today || 0} today · {c.upcoming || 0} upcoming
                </span>
              </div>

              {(c.overdue || 0) + (c.due_today || 0) + (c.upcoming || 0) + (c.unscheduled || 0) === 0 ? (
                <p className="text-sm text-[#8A857A] py-4 text-center">No open rep action items — set a due date on a meeting's action to see it here.</p>
              ) : (
                <>
                  {reminders.overdue.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-[#C23A00] font-semibold mb-2">Overdue ({reminders.overdue.length})</div>
                      <div className="space-y-2">{reminders.overdue.map((it) => <ReminderRow key={`${it.meeting_id}-${it.index}`} item={it} tone="#C23A00" onDone={markDone} />)}</div>
                    </div>
                  )}
                  {reminders.due_today.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-[#6B5600] font-semibold mb-2">Due today ({reminders.due_today.length})</div>
                      <div className="space-y-2">{reminders.due_today.map((it) => <ReminderRow key={`${it.meeting_id}-${it.index}`} item={it} tone="#6B5600" onDone={markDone} />)}</div>
                    </div>
                  )}
                  {reminders.upcoming.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-[#8A6D00] font-semibold mb-2">Upcoming ({reminders.upcoming.length})</div>
                      <div className="space-y-2">{reminders.upcoming.map((it) => <ReminderRow key={`${it.meeting_id}-${it.index}`} item={it} tone="#8A6D00" onDone={markDone} />)}</div>
                    </div>
                  )}
                  {reminders.unscheduled.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-[#8A857A] font-semibold mb-2">No due date ({reminders.unscheduled.length})</div>
                      <div className="space-y-2">{reminders.unscheduled.map((it) => <ReminderRow key={`${it.meeting_id}-${it.index}`} item={it} tone="#8A857A" onDone={markDone} />)}</div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
